import type { LangfuseTraceClient } from 'langfuse'

import { getBrowserLangfuseTracingClient, buildBrowserLangfuseBaseMetadata } from './langfuse.client'
import { ANAMNESIS_PIPELINE_SPANS } from './pipeline.spans'
import { ensureConsultationTraceId, clearConsultationTraceId } from './trace-context'
import { sanitizeLangfuseMetadata } from './telemetry.sanitizer'
import { runTelemetrySafely } from './telemetry.helpers'
import type { AnamnesisConsultationTelemetryContext } from './telemetry-metadata'

const activeRootTraces = new Map<string, LangfuseTraceClient>()

export const ensureConsultationRootTrace = (context: AnamnesisConsultationTelemetryContext): string | null => {
  const consultationId = context.consultation_id?.trim()
  if (!consultationId) return null

  let traceId: string | null = null
  runTelemetrySafely(() => {
    const lf = getBrowserLangfuseTracingClient()
    if (!lf) return

    traceId = ensureConsultationTraceId(consultationId)
    if (activeRootTraces.has(consultationId)) return

    const trace = lf.trace({
      id: traceId,
      name: ANAMNESIS_PIPELINE_SPANS.ROOT,
      sessionId: consultationId,
      userId: context.doctor_id ?? undefined,
      tags: ['synap-browser', 'anamnesis-consultation'],
      metadata: sanitizeLangfuseMetadata(
        buildBrowserLangfuseBaseMetadata({
          consultation_id: consultationId,
          ...(context.browser ? { browser: context.browser } : {}),
          ...(context.device ? { device: context.device } : {}),
          ...(context.language ? { language: context.language } : {}),
          ...(context.offline_mode != null ? { offline_mode: context.offline_mode } : {}),
          ...(context.fallback_mode ? { fallback_mode: context.fallback_mode } : {}),
        }),
      ),
    })
    activeRootTraces.set(consultationId, trace)
  })
  return traceId
}

export const getConsultationRootTrace = (consultationId: string): LangfuseTraceClient | null =>
  activeRootTraces.get(consultationId) ?? null

export const endConsultationRootTrace = (consultationId: string, metadata?: Record<string, unknown>): void => {
  runTelemetrySafely(() => {
    const trace = activeRootTraces.get(consultationId)
    if (!trace) return
    if (metadata && Object.keys(metadata).length) {
      trace.update({
        metadata: sanitizeLangfuseMetadata(
          buildBrowserLangfuseBaseMetadata(metadata as Record<string, string | number | boolean | null>),
        ),
      })
    }
    activeRootTraces.delete(consultationId)
    clearConsultationTraceId(consultationId)
  })
}
